import { toast } from "@js/shared/toast";
import { SAFETY_CHECK_ACCEPTED_IMAGE_TYPES, SAFETY_CHECK_MAX_IMAGE_COUNT, SAFETY_CHECK_MAX_IMAGE_SIZE_BYTES } from "@js/shared/model/safetyCheckImage";

export type SafetyCheckUploadedImage = { key: string; src: string };
type UploadResponse = { success?: boolean; error?: string; key?: string; url?: string };
type SafetyCheckImageUploadHost = {
  images: Array<{ key: string; src: string; orientation?: 'portrait' | 'landscape' | null }>;
  uploading: boolean;
};

export async function uploadSafetyCheckImage(
  host: SafetyCheckImageUploadHost,
  event: Event,
  replaceIndex?: number,
  onAdded?: (image: SafetyCheckUploadedImage) => Promise<void>,
  resolveImageSrc?: (file: File, uploadedSrc: string) => string
): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file || host.uploading) {
    return;
  }
  if (!SAFETY_CHECK_ACCEPTED_IMAGE_TYPES.includes(file.type) || file.size > SAFETY_CHECK_MAX_IMAGE_SIZE_BYTES) {
    toast('JPEG、PNG、HEICの10MB以下の画像を選択してください', 'alert');
    return;
  }
  if (replaceIndex == null && host.images.length >= SAFETY_CHECK_MAX_IMAGE_COUNT) {
    toast('画像は5枚までです', 'alert');
    return;
  }

  host.uploading = true;
  const body = new FormData();
  body.append('file', file);
  body.append('classification', 'evaluation_safety_check');
  try {
    const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '';
    const response = await fetch('/blobs', { method: 'POST', headers: { 'X-CSRF-Token': token }, body });
    const result = await response.json() as UploadResponse;
    if (!response.ok || !result.success || !result.key || !result.url) {
      throw new Error(result.error || '画像をアップロードできませんでした');
    }
    const image: SafetyCheckUploadedImage = {
      key: result.key,
      src: resolveImageSrc?.(file, result.url) || result.url
    };
    if (replaceIndex == null) {
      host.images.push(image);
      await onAdded?.(image);
    } else {
      host.images.splice(replaceIndex, 1, image);
    }
  } catch {
    input.dispatchEvent(new CustomEvent('safety-image-upload-failed', {
      bubbles: true,
      detail: { source: replaceIndex == null ? 'add' : 'replace' }
    }));
  } finally {
    host.uploading = false;
  }
}
